

































import {Vue, Component, Prop, Emit, Watch} from 'nuxt-property-decorator'
import {Editor, EditorContent} from 'tiptap'
import {
  Blockquote,
  Heading,
  OrderedList,
  BulletList,
  ListItem,
  Bold,
  Italic,
  Link,
  Strike,
  History,
} from 'tiptap-extensions'
import sanitizeHtml from 'sanitize-html'
import parse5 from 'parse5'

import InputRules from './extensions/InputRules'
import Dropper from './extensions/Dropper'
import Caser from './extensions/Caser'
import FileNode from './nodes/File'
import Image from './nodes/Image'
import Url from './nodes/Url'
import Iframe from './nodes/Iframe'
import Visme from './nodes/Visme'
import './visme.js'
import HtmlEditorMenu from './HtmlEditorMenu.vue'
import ChartModal from './ChartModal.vue'

const sanitize = (s: string) => {
  return sanitizeHtml(s, {
    disallowedTagsMode: 'escape',
    allowedTags: sanitizeHtml.defaults.allowedTags.concat([
      'h2',
      'img',
      'div',
      'svg',
      'span',
      'g',
      'defs',
      'clippath',
      'path',
      'picture',
      'iframe',
    ]),
    allowedAttributes: {
      div: [
        'class',
        'data-url',
        'data-w',
        'data-h',
        'data-domain',
        'src',
        'text',
      ],
      a: ['href', 'target', 'class', 'style'],
      img: ['src', 'alt', 'loading'],
      iframe: ['src', 'style', 'frameborder', 'loading'],
      picture: ['class'],
      source: ['srcset', 'media'],
      svg: ['xmlns', 'width', 'height', 'fill', 'viewbox'],
      g: ['clip-path'],
      defs: [],
      clippath: ['id'],
      path: ['fill', 'd'],
    },
    allowedSchemesByTag: {
      img: ['http', 'https', 'data'],
    },
  })
}

const replaceDataAttrs = (html) => {
  const regex = /data(?!-).*=/gm
  return html.replace(regex, 'data-')
}

const replaceIframes = (parsed) => {
  parsed.childNodes.forEach((node, index) => {
    // find div.iframe-parent
    if (
      node.tagName === 'div' &&
      node.attrs.find(
        ({name, value}) => name === 'class' && value === 'iframe-parent'
      )
    ) {
      // find img
      node.childNodes.forEach((_node) => {
        if (_node.tagName === 'img') {
          // have to replace class because the current, 'dw-alt' class is blacklisted from the editor
          _node.attrs = _node.attrs.map((attr) => {
            if (attr.name === 'class') {
              return {name: 'class', value: 'dw-chart'}
            }
            return attr
          })
          // replace nodes
          parsed.childNodes[index] = _node
        }
      })
    }
  })
  return parse5.serialize(parsed)
}

@Component({
  components: {EditorContent, HtmlEditorMenu, ChartModal},
})
class HtmlEditor extends Vue {
  @Prop({type: String, default: ''})
  value: string

  @Prop({type: String, default: null})
  token: string

  @Prop({type: String, default: null})
  imageUploadUrl: string

  @Prop({type: String, default: null})
  fileUploadUrl: string

  @Prop({type: Boolean, default: () => false})
  enableCharts: boolean

  editor: any = null
  viewSource: boolean = false
  bodyScrollLock = null
  chartModalVisible = false

  get html() {
    return this.value
  }

  @Watch('value')
  updateEditorContents() {
    if (this.value !== sanitize(this.editor.getHTML())) {
      this.editor.setContent(this.value)
    }
  }

  mounted() {
    const {token = null, imageUploadUrl = null, fileUploadUrl = null} = this
    const extensions = [
      new InputRules(),
      new Blockquote(),
      new Heading({levels: [2, 3]}),
      new BulletList(),
      new OrderedList(),
      new ListItem(),
      new Bold(),
      new Italic(),
      new Link(),
      new Strike(),
      new History(),
      new FileNode(),
      new Image(),
      new Url(),
      new Dropper({token, imageUploadUrl, fileUploadUrl}),
      new Caser(),
    ]

    if (this.enableCharts) {
      extensions.push(new Iframe(), new Visme())
    }

    this.editor = new Editor({
      editorProps: {
        transformPastedHTML(s: string) {
          if (!this.enableCharts) {
            // traverse through all childNodes and replace iframe-parent node with child img node
            const parsed = parse5.parseFragment(s)
            return replaceIframes(parsed)
          } else {
            return s
          }
        },
      },
      content: this.html,
      extensions,
      onUpdate: ({getHTML}) => {
        this.update(sanitize(getHTML()))
        window.setup_visme()
      },
      onBlur: ({state}) => {
        const lastPosition = state.selection.$from.pos
        localStorage.setItem('htmleditor.lastCursorPosition', `${lastPosition}`)
      },
      onPaste: () => console.log('Pasted!'),
    })
  }

  beforeDestroy() {
    this.editor.destroy()
  }

  @Emit('input')
  update(html) {
    window.setup_visme()
    return html
  }

  toggleSourceView() {
    if (this.viewSource) {
      this.update(sanitize(this.editor.getHTML()))
    }

    this.viewSource = !this.viewSource
    window.setup_visme()
  }

  onFileSelection(files = []) {
    if (files.length) {
      this.editor.commands.dropper(files)
    }
  }

  toggleChartModal() {
    this.chartModalVisible = !this.chartModalVisible
  }
}

export default HtmlEditor
